import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { MultiServiceEntry } from '../../api/types';

const SERVICE_LABELS: Record<string, string> = {
  blob_storage: 'Blob Storage',
  logic_apps: 'Logic Apps',
  functions: 'Azure Functions',
  app_service: 'App Service',
  bandwidth: 'Bandwidth & Egress',
  log_analytics: 'Log Analytics',
  sql_database: 'SQL Database',
  firewall: 'Azure Firewall',
  networking: 'Networking',
  data_factory: 'Data Factory',
  virtual_desktop: 'Azure Virtual Desktop',
  front_door: 'Azure Front Door',
  managed_disks: 'Managed Disks',
  key_vault: 'Key Vault',
  azure_devops: 'Azure DevOps',
  site_recovery: 'Site Recovery',
  aks: 'AKS',
  sql_managed_instance: 'SQL Managed Instance',
  application_gateway: 'Application Gateway',
  vpn_gateway: 'VPN Gateway',
  bastion: 'Azure Bastion',
  azure_backup: 'Azure Backup',
  azure_openai: 'Azure OpenAI',
};

const SERVICE_ICONS: Record<string, string> = {
  blob_storage: 'folder_open',
  logic_apps: 'account_tree',
  functions: 'bolt',
  app_service: 'web',
  bandwidth: 'swap_horiz',
  log_analytics: 'monitor_heart',
  sql_database: 'database',
  firewall: 'shield',
  networking: 'hub',
  data_factory: 'schema',
  virtual_desktop: 'desktop_windows',
  front_door: 'public',
  managed_disks: 'storage',
  key_vault: 'lock',
  azure_devops: 'code',
  site_recovery: 'cloud_sync',
  aks: 'lan',
  sql_managed_instance: 'database',
  application_gateway: 'call_split',
  vpn_gateway: 'vpn_key',
  bastion: 'screen_share',
  azure_backup: 'backup',
  azure_openai: 'smart_toy',
};

const SERVICE_ROUTES: Record<string, string> = {
  blob_storage: '/blob-storage',
  logic_apps: '/logic-apps',
  functions: '/functions',
  app_service: '/app-service',
  bandwidth: '/bandwidth',
  log_analytics: '/log-analytics',
  sql_database: '/sql-database',
  firewall: '/firewall',
  networking: '/networking',
  data_factory: '/data-factory',
  virtual_desktop: '/virtual-desktop',
  front_door: '/front-door',
  managed_disks: '/managed-disks',
  key_vault: '/key-vault',
  azure_devops: '/azure-devops',
  site_recovery: '/site-recovery',
  aks: '/aks',
  sql_managed_instance: '/sql-managed-instance',
  application_gateway: '/application-gateway',
  vpn_gateway: '/vpn-gateway',
  bastion: '/bastion',
  azure_backup: '/azure-backup',
  azure_openai: '/azure-openai',
};

interface CombinedEstimatePanelProps {
  services: MultiServiceEntry[];
  totalGbp: number;
  isUpdating?: boolean;
}

function ServiceCard({ entry, defaultOpen }: { entry: MultiServiceEntry; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const label = SERVICE_LABELS[entry.service] ?? entry.service;
  const icon = SERVICE_ICONS[entry.service] ?? 'cloud';
  const route = SERVICE_ROUTES[entry.service];
  const monthly = entry.calculation?.monthly_estimate ?? null;

  return (
    <div
      className="rounded-sm overflow-hidden"
      style={{ border: '1px solid var(--outline-variant)' }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:opacity-80"
        style={{ backgroundColor: 'var(--surface-container-high)' }}
      >
        <span
          className="material-symbols-outlined text-[18px] shrink-0"
          style={{ color: '#0078d4', fontVariationSettings: "'FILL' 1" }}
        >
          {icon}
        </span>
        <span className="flex-1 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {label}
        </span>
        {monthly != null && (
          <span className="font-mono text-sm font-bold" style={{ color: '#0078d4' }}>
            £{monthly.toFixed(2)}/mo
          </span>
        )}
        {monthly == null && (
          <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>not calculated</span>
        )}
        <span className="material-symbols-outlined text-[16px]" style={{ color: 'var(--text-tertiary)' }}>
          {open ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {open && (
        <div className="px-4 py-3 flex flex-col gap-3" style={{ borderTop: '1px solid var(--outline-variant)' }}>
          {entry.clarifications && entry.clarifications.length > 0 && (
            <div
              className="flex items-start gap-2 p-2 rounded-sm text-xs"
              style={{ backgroundColor: 'var(--surface-container)', color: 'var(--text-secondary)' }}
            >
              <span className="material-symbols-outlined text-[14px] shrink-0 mt-0.5" style={{ color: '#e8b000' }}>
                info
              </span>
              <span>
                Used defaults for: {entry.clarifications.join(', ')}. Open the calculator to refine.
              </span>
            </div>
          )}

          {monthly != null && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-tertiary)' }}>
                Monthly estimate
              </p>
              <p className="text-2xl font-bold font-mono" style={{ color: '#0078d4' }}>
                £{monthly.toFixed(2)}
                <span className="text-sm font-normal" style={{ color: 'var(--text-tertiary)' }}>/mo</span>
              </p>
            </div>
          )}

          {route && (
            <Link
              to={route}
              className="inline-flex items-center gap-1.5 text-xs font-semibold py-1.5 px-3 rounded-sm transition-colors"
              style={{
                color: '#0078d4',
                backgroundColor: 'rgba(0,120,212,0.08)',
                border: '1px solid rgba(0,120,212,0.25)',
              }}
            >
              <span className="material-symbols-outlined text-[14px]">open_in_new</span>
              Open {label} calculator
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export function CombinedEstimatePanel({ services, totalGbp, isUpdating }: CombinedEstimatePanelProps) {
  const serviceNames = services.map(s => SERVICE_LABELS[s.service] ?? s.service);

  return (
    <div
      className="rounded-sm overflow-hidden"
      style={{
        border: isUpdating ? '2px solid var(--accent-amber, #e8b000)' : '1px solid var(--outline-variant)',
        transition: 'border-color 0.3s ease',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 flex flex-col gap-1"
        style={{ backgroundColor: 'var(--surface-container-highest)' }}
      >
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: 'var(--text-tertiary)' }}>
              Combined estimate
            </p>
            <p className="text-3xl font-bold font-mono" style={{ color: '#0078d4' }}>
              £{totalGbp.toFixed(2)}
              <span className="text-sm font-normal" style={{ color: 'var(--text-tertiary)' }}>/mo</span>
            </p>
          </div>
          {isUpdating && (
            <span className="material-symbols-outlined text-[20px] animate-spin" style={{ color: '#e8b000' }}>
              progress_activity
            </span>
          )}
        </div>
        <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          {serviceNames.join(' + ')}
        </p>
      </div>

      {/* Per-service cards */}
      <div className="flex flex-col gap-2 p-3" style={{ backgroundColor: 'var(--surface-container)' }}>
        {services.map((entry, i) => (
          <ServiceCard key={entry.service} entry={entry} defaultOpen={i === 0} />
        ))}
      </div>

      <div
        className="px-4 py-2 text-[10px]"
        style={{ color: 'var(--text-tertiary)', borderTop: '1px solid var(--outline-variant)' }}
      >
        Estimate only — always verify via the Azure Pricing Calculator.
      </div>
    </div>
  );
}
